/**
 * Document title extraction — single source of truth.
 *
 * P1.3 from the 2026-05-23 codex audit: both `lib/render/index.ts` (for
 * meta.title) and `App.svelte` (for docTitle / export filename / browser
 * title) were using `source.match(/^#\s+(.+)/m)`, which only matches ATX
 * H1. Setext H1 (`Title\n=====`) renders correctly in the preview but
 * became "Untitled" everywhere else. Consolidating extraction here means
 * both consumers stay in lock-step.
 *
 * Order:
 *   1. First ATX H1 (`# Title`) — including optional closing `#` characters
 *      that the spec allows on ATX headings
 *   2. First setext H1 — any line followed by a `=====` line (one or more
 *      `=` characters, allowing trailing whitespace)
 *   3. Fallback to "Untitled"
 *
 * Setext detection is conservative: the underline line must contain ONLY
 * `=` (one or more) plus optional trailing whitespace. The title line
 * itself is trimmed but otherwise taken verbatim — setext headings carry
 * their inline-markup as-is (no closing `#` to strip).
 */

const ATX_LINE_RE = /^\s*#\s+(.+?)\s*#*\s*$/

export function getDocTitle(source: string): string {
  // We do two ordered passes so ATX wins globally over setext (matches
  // the audit spec — `# X` later in the doc beats a setext earlier).
  // The fenced-code state is rebuilt in each pass to keep both branches
  // honest (a `#` inside a fence is content, not a heading).
  const lines = source.split('\n')

  // Pass 1: ATX.
  {
    let inFence = false
    for (const line of lines) {
      if (/^```/.test(line)) {
        inFence = !inFence
        continue
      }
      if (inFence) continue
      const atx = line.match(ATX_LINE_RE)
      if (atx?.[1]) {
        const t = atx[1].trim()
        if (t) return t
      }
    }
  }

  // Pass 2: setext H1 (non-empty title line followed by `=====`).
  {
    let inFence = false
    for (let i = 0; i < lines.length - 1; i++) {
      const line = lines[i]!
      if (/^```/.test(line)) {
        inFence = !inFence
        continue
      }
      if (inFence) continue
      if (!line.trim()) continue
      const next = lines[i + 1]!
      if (/^=+\s*$/.test(next)) {
        return line.trim()
      }
    }
  }

  return 'Untitled'
}
